import React, { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import useApi from '@/hooks/useApi';
import NotificationDropdown from './NotificationDropdown';
import type { NotificationResponse } from '@/types/notification';

const NotificationBell: React.FC = () => {
    const { t } = useTranslation();
    const [showDropdown, setShowDropdown] = useState(false);
    const [notifications, setNotifications] = useState<NotificationResponse>({
        notifications: [],
        count: 0,
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const api = useApi();

    const fetchNotifications = async () => {
        try {
            const response = await api.get('/notifications/api/notifications/');
            setNotifications(response.data);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    };

    useEffect(() => {
        fetchNotifications();

        // Poll for new notifications every 30 seconds
        const interval = setInterval(fetchNotifications, 30000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!showDropdown) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    const handleMarkAsRead = async (notificationId: number) => {
        try {
            await api.post(
                `/notifications/api/notifications/${notificationId}/read/`
            );
            await fetchNotifications();
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await api.post('/notifications/api/notifications/mark-all-read/');
            await fetchNotifications();
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="relative rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white"
                aria-label={t('notifications.title')}
            >
                <Bell size={20} />
                {notifications.count > 0 && (
                    <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                        {notifications.count > 9 ? '9+' : notifications.count}
                    </span>
                )}
            </button>

            {showDropdown && (
                <NotificationDropdown
                    notifications={notifications.notifications}
                    onClose={() => setShowDropdown(false)}
                    onMarkAsRead={handleMarkAsRead}
                    onMarkAllAsRead={handleMarkAllAsRead}
                />
            )}
        </div>
    );
};

export default NotificationBell;
